import { isAvailableInSelectedMarkets } from "@utils/markets/availability";
import type { FilterSelection } from "@interfaces/filters/domain";
import type { ActiveGroupSnapshot } from "./selectionListener";

type PruneItem =
  | { kind: "filter-removed"; name: string }
  | { kind: "filter-adjusted"; name: string; removedOptionCount: number }
  | { kind: "segment-removed"; name: string };

/**
 * Comma-joined display names for the selected markets, or null when the copy
 * must fall back to a generic phrase (more than three markets, or any name
 * unresolvable). The all-or-nothing rule keeps a stale/incomplete lookup from
 * naming only some of the selected markets, which reads as wrong data.
 *
 * Copy contract (spec 2026-08-11-market-prune-copy-design, revised
 * 2026-08-11): no em/en dashes in generated copy; two-line messages with the
 * item on line 1 and the reason on line 2; no consequence sentence.
 */
function marketNames(
  marketIds: number[],
  namesById: Record<number, string>,
): string | null {
  if (marketIds.length > 3) return null;
  const names = marketIds.map((id) => namesById[id]).filter((n): n is string => !!n);
  if (names.length !== marketIds.length) return null;
  return names.join(", ");
}

function reasonPhrase(names: string | null, marketCount: number): string {
  if (names == null) return "in your selected markets";
  if (marketCount === 1) return `in ${names}`;
  return `in all selected markets (${names})`;
}

function formatSingle(item: PruneItem, phrase: string): string {
  switch (item.kind) {
    case "filter-removed":
      return `"${item.name}" filter removed\nNot available ${phrase}.`;
    case "filter-adjusted": {
      const n = item.removedOptionCount;
      return `"${item.name}" filter adjusted\n${n} selected option${n === 1 ? " is" : "s are"} not available ${phrase}.`;
    }
    case "segment-removed":
      return `"${item.name}" segment removed\nNot applicable ${phrase}.`;
  }
}

function formatBullet(item: PruneItem): string {
  switch (item.kind) {
    case "filter-removed":
      return `• "${item.name}" filter removed`;
    case "filter-adjusted": {
      const n = item.removedOptionCount;
      return `• "${item.name}" filter: ${n} option${n === 1 ? "" : "s"} removed`;
    }
    case "segment-removed":
      return `• "${item.name}" segment removed`;
  }
}

/**
 * Compute the user-facing message for selections that market pruning removed.
 *
 * Recomputes applicability from the PRE-prune selection against the
 * POST-prune market list rather than diffing prune output, so removals with
 * other causes (pool membership, seeding, caps) never surface here. Returns
 * null when nothing market-related was removed — the caller shows no toast.
 */
export function describeMarketPrune(
  before: { filters: FilterSelection[]; segments: string[] },
  marketIds: number[],
  snapshot: ActiveGroupSnapshot,
): string | null {
  if (marketIds.length === 0) return null;

  const removed: PruneItem[] = [];
  const adjusted: PruneItem[] = [];
  for (const selection of before.filters) {
    const entry = snapshot.filterCatalogByCode[selection.code];
    if (!entry) continue;
    if (!isAvailableInSelectedMarkets(entry.markets, marketIds)) {
      removed.push({ kind: "filter-removed", name: entry.name });
      continue;
    }
    if (selection.kind === "OPTION") {
      const removedOptionCount = selection.options.filter((code) => {
        const optionMarkets = entry.optionMarkets[code];
        return (
          optionMarkets != null &&
          !isAvailableInSelectedMarkets(optionMarkets, marketIds)
        );
      }).length;
      if (removedOptionCount > 0) {
        adjusted.push({ kind: "filter-adjusted", name: entry.name, removedOptionCount });
      }
    }
  }

  const segments: PruneItem[] = before.segments
    .map((code) => snapshot.segmentCatalogByCode[code])
    .filter(
      (entry): entry is { label: string; type: string; markets: number[]; accessible: boolean } =>
        entry != null &&
        entry.type !== "SYSTEM" &&
        !isAvailableInSelectedMarkets(entry.markets, marketIds),
    )
    .map((entry) => ({ kind: "segment-removed" as const, name: entry.label }));

  const items = [...removed, ...adjusted, ...segments];
  if (items.length === 0) return null;

  const names = marketNames(marketIds, snapshot.marketNamesById);
  if (items.length === 1) {
    return formatSingle(items[0], reasonPhrase(names, marketIds.length));
  }
  return [
    `Selections adjusted for ${names ?? "your selected markets"}:`,
    ...items.map(formatBullet),
  ].join("\n");
}
